import { Component, ViewChild } from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { HtmlEditorService, ImageService, LinkService, RichTextEditorModule, ToolbarService } from '@syncfusion/ej2-angular-richtexteditor';
import { ANGULAR_MODULES, GENERAL_COMPONENTS, Operational_COMPONENTS, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { WebInquirySettingsService } from 'src/app/services/WebInquirySettings.service';
import { Subject, takeUntil, Subscription, filter } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { EActionTypes, EToaster, EUserSecurityFunctionID } from "src/app/Shared/enum";
import { IUserPermissionHandler } from "src/app/Shared/model/CommonModel";
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { UserPermissionHandlerDirective } from "src/app/Shared/userPermissionHandler.directive";
import { forkJoin } from 'rxjs';
import { UserService } from 'src/app/services/User.service';

@Component({
  selector: 'app-ops-wi-settings',
  standalone: true,
  imports: [Operational_COMPONENTS, SYNCFUSION_MODULES, ANGULAR_MODULES, GENERAL_COMPONENTS, FormsModule, RichTextEditorModule, WordReplacePipe, ReactiveFormsModule, UserPermissionHandlerDirective, LoaderComponent],
  providers: [ToolbarService, LinkService, ImageService, HtmlEditorService, FilterService],
  templateUrl: './ops-wi-settings.html',
  styleUrl: './ops-wi-settings.scss'
})
export class OpsWiSettings {
  private destroy$ = new Subject<void>();
  isCollapsed = true;
  generalMsg = true;
  logoPreview1: string = './assets/images/NoPhoto.svg';
  logoPreview2: string = './assets/images/NoPhoto.svg';
  selectedTab: 'current' | 'all' | 'inactive' = 'current';
  vendorData: any[] = [];
  vendorType: ['']
  selectedVendorIds: number[] = [];
  public selectionSettings = {
    type: 'Multiple',
    checkboxOnly: true,
    persistSelection: true
  };
  private isFilteringOrSearching = false;
  private filterCleared = false;
  loaderIcon: Promise<boolean> = Promise.resolve(false);
  webinquirySettingsForm!: FormGroup;
  vendorMessageDraft: string = '';
  private subscriptions: Subscription[] = [];
  @ViewChild('gridVendors', { static: false }) public gridVendors: GridComponent;
  public toolbarOptions = {
    items: [
      'Bold', 'Italic', 'Underline', 'StrikeThrough', '|', 'OrderedList', 'UnorderedList', '|', 'CreateLink', '|', 'SourceCode', 'Undo', 'Redo'
    ]
  };
  activeTab: 'general' | 'vendor' | 'rentalPayment' = 'general';
  userList: any[] = [];

  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.WISettings, Action: EActionTypes.VIEW };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.WISettings, Action: EActionTypes.EDIT };

  constructor(
    private fb: FormBuilder,
    private webInquirySettingsService: WebInquirySettingsService,
    private toastr: ToastrService,
    private userService: UserService,
    private router: Router,
    private sharedHTMLSettings: SharedHTMLSettingsDataService
  ) {
    this.webinquirySettingsForm = this.fb.group({
      generalMessage: [''],
      image1FileName: [''],
      uploadedImage1Content: [''],
      deleteImage1: [false],
      image2FileName: [''],
      uploadedImage2Content: [''],
      deleteImage2: [false],
      vendorMessage: ['', [Validators.maxLength(1000)]],
      vendorTab: 'current',
      vendorType: [''],
      onlinePaymentUserId: [0]
    });
  }

  ngOnInit(): void {
    this.loaderIcon = Promise.resolve(true);
    this.loadDefaultWebInquiryUserId();
    this.loadUserList();
    this.loadWebInquiryVendors();
    this.loadGeneralSettings();
    this.webinquirySettingsForm
      .get('vendorMessage')
      ?.valueChanges.subscribe(value => {
        this.vendorMessageDraft = value;
      });
  }

  loadDefaultWebInquiryUserId(): void {
    this.webInquirySettingsService.getDefaultWebInquiryUserId().pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userId: number) => {
          const onlinePaymentUser = userId || 0;
          this.webinquirySettingsForm.patchValue({
            onlinePaymentUserId: onlinePaymentUser
          });
        },
        error: (err) => {
          console.error('Failed to load Online Payment User ID', err);
        }
      });
  }

  loadUserList() {
    this.userService.getUserList().pipe(takeUntil(this.destroy$)).subscribe({
      next: (users: any) => {
        this.userList = users.filter(u => !u.UserInactive);
      },
      error: (err) => {
        console.error('Failed to load user list', err);
      }
    });
  }

  loadGeneralSettings(): void {
    this.webInquirySettingsService
      .getWebInquiryGeneralSettings()
      .subscribe({
        next: (res) => {
          if (res) {
            this.logoPreview1 = res.UploadedImageBase64
              ? `data:image/png;base64,${res.UploadedImageBase64}`
              : res.ImageName || './assets/images/NoPhoto.svg';

            this.logoPreview2 = res.UploadedImage2Base64
              ? `data:image/png;base64,${res.UploadedImage2Base64}`
              : res.Image2Name || './assets/images/NoPhoto.svg';

            this.webinquirySettingsForm.patchValue({
              generalMessage: res.GeneralMessage || '',
              image1FileName: res.ImageName || '',
              uploadedImage1Content: res.UploadedImageBase64 || '',
              deleteImage1: res.DeleteFlag || false,
              image2FileName: res.Image2Name || '',
              uploadedImage2Content: res.UploadedImage2Base64 || '',
              deleteImage2: res.DeleteImage2Flag || false
            });
          }

          this.loaderIcon = Promise.resolve(false);
        },
        error: () => {
          this.loaderIcon = Promise.resolve(false);
        }
      });
  }

  loadVendorMessage(vendorId: number): void {
    this.webInquirySettingsService
      .getVendorMessage(vendorId)
      .subscribe(res => {
        this.webinquirySettingsForm.patchValue({
          vendorMessage: res?.messageForVendor || ''
        });
      });
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  activeMsg(type: 'general' | 'vendor' | 'rentalPayment'): void {
    this.activeTab = type;
    this.generalMsg = type === 'general';
  }

  onVendorTabChange(tab: 'current' | 'all' | 'inactive'): void {
    this.fullyResetGridSelection();
    this.selectedTab = tab;
    let status = 1;
    if (tab === 'all') status = 2;
    if (tab === 'inactive') status = 3;
    this.loadWebInquiryVendors(status);
  }

  onVendorSelected(_args?: any): void {
    setTimeout(() => this.syncSelectedVendorIds());
  }

  onVendorDeselected(_args?: any): void {
    setTimeout(() => this.syncSelectedVendorIds());
  }

  private syncSelectedVendorIds(): void {
    if (!this.gridVendors) return;
    const records = this.gridVendors.getSelectedRecords() as any[];
    this.selectedVendorIds = records.map(r => r.VendorId);
  }

  loadWebInquiryVendors(status = 1): void {
    this.webInquirySettingsService
      .getWebInquiryVendors(status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.vendorData = res || [];
          this.loaderIcon = Promise.resolve(false);
        },
        error: () => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error(
            'Failed to load vendors',
            'Error',
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  onImageSelected(event: Event, imageNumber: 1 | 2): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        if (imageNumber === 1) {
          this.logoPreview1 = reader.result as string;
          this.webinquirySettingsForm.patchValue({
            uploadedImage1Content: base64,
            image1FileName: file.name,
            deleteImage1: false
          });
        } else {
          this.logoPreview2 = reader.result as string;
          this.webinquirySettingsForm.patchValue({
            uploadedImage2Content: base64,
            image2FileName: file.name,
            deleteImage2: false
          });
        }
        this.webinquirySettingsForm.markAsDirty();
      };
      reader.readAsDataURL(file);
    }
  }

  addPhoto(imageNumber: 1 | 2): void {
    const fileInput = document.getElementById(`fileInput${imageNumber}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  removeImage(imageNumber: 1 | 2): void {
    if (imageNumber === 1) {
      this.logoPreview1 = './assets/images/NoPhoto.svg';
      this.webinquirySettingsForm.patchValue({
        uploadedImage1Content: '',
        image1FileName: '',
        deleteImage1: true
      });
    } else {
      this.logoPreview2 = './assets/images/NoPhoto.svg';
      this.webinquirySettingsForm.patchValue({
        uploadedImage2Content: '',
        image2FileName: '',
        deleteImage2: true
      });
    }
    this.webinquirySettingsForm.markAsDirty();
  }

  saveGeneralSettings(): void {
    this.loaderIcon = Promise.resolve(true);
    const payload = {
      GeneralMessage: this.webinquirySettingsForm.get('generalMessage')?.value,
      ImageName: this.webinquirySettingsForm.get('image1FileName')?.value,
      UploadedImageBase64: this.webinquirySettingsForm.get('uploadedImage1Content')?.value,
      DeleteFlag: this.webinquirySettingsForm.get('deleteImage1')?.value,
      Image2Name: this.webinquirySettingsForm.get('image2FileName')?.value,
      UploadedImage2Base64: this.webinquirySettingsForm.get('uploadedImage2Content')?.value,
      DeleteImage2Flag: this.webinquirySettingsForm.get('deleteImage2')?.value,
      ClientId: 123
    };
    this.webInquirySettingsService
      .saveWebInquiryGeneralSettings(payload)
      .subscribe({
        next: () => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.success(
            'WI General message saved successfully',
            'Saved',
            this.sharedHTMLSettings.ToasterConfig
          );
          this.webinquirySettingsForm.markAsPristine();
        },
        error: () => {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error(
            'Failed to save WI General settings',
            'Error',
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  saveVendorMessage(): void {
    if (!this.selectedVendorIds.length) {
      this.toastr.warning(
        'Please select at least one vendor',
        'Warning',
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const vendorMessageControl = this.webinquirySettingsForm.get('vendorMessage');

    if (vendorMessageControl?.hasError('maxlength')) {
      this.toastr.warning(
        'Vendor message cannot exceed 900 characters.',
        'Warning',
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    this.loaderIcon = Promise.resolve(true);
    const message = this.webinquirySettingsForm.get('vendorMessage')?.value;

    const requests = this.selectedVendorIds.map(vendorId =>
      this.webInquirySettingsService.saveVendorMessage({
        vendorId,
        messageForVendor: message
      })
    );

    forkJoin(requests).subscribe({
      next: () => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.success(
          'Vendor message saved successfully',
          'Saved',
          this.sharedHTMLSettings.ToasterConfig
        );
        this.webinquirySettingsForm.patchValue({ vendorMessage: '' });
        this.vendorMessageDraft = '';
        this.fullyResetGridSelection();

        this.webinquirySettingsForm.patchValue({
          vendorTab: 'current',
          vendorType: ''
        });
        this.webinquirySettingsForm.markAsPristine();
      },
      error: () => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error(
          'Failed to save vendor message',
          'Error',
          this.sharedHTMLSettings.ToasterConfig
        );
      }
    });
  }

  onSave(): void {
    if (!this.webinquirySettingsForm.dirty) {
      this.toastr.warning(
        'No changes to save',
        'Warning',
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    if (this.activeTab === 'vendor') {
      if (!this.selectedVendorIds.length) {
        this.toastr.warning(
          'Please select a vendor first',
          'Warning',
          this.sharedHTMLSettings.ToasterConfig
        );
        return;
      }

      this.saveVendorMessage();
      return;
    }

    if (this.activeTab === 'rentalPayment') {
      if (!this.webinquirySettingsForm.get('onlinePaymentUserId')?.dirty &&
        !this.webinquirySettingsForm.dirty) {
        this.toastr.warning(
          'No changes to save',
          'Warning',
          this.sharedHTMLSettings.ToasterConfig
        );
        return;
      }

      this.saveOnlinePaymentUser();
      return;
    }

    this.saveGeneralSettings();
  }

  saveOnlinePaymentUser(): void {
    const userId = Number(this.webinquirySettingsForm.get('onlinePaymentUserId')?.value ?? 0);

    this.loaderIcon = Promise.resolve(true);
    this.webInquirySettingsService.saveWebInquiryUserId(userId).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loaderIcon = Promise.resolve(false);

          if (res?.Success) {
            this.toastr.success(res.Message || 'Online Payment User saved successfully', 'Success', this.sharedHTMLSettings.ToasterConfig);
            this.webinquirySettingsForm.markAsPristine();
          } else {
            this.toastr.error(res?.Message || 'Failed to save Online Payment User', 'Error', this.sharedHTMLSettings.ToasterConfig);
          }
        },
        error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          console.error('Failed to save Online Payment User', err);
          this.toastr.error('Failed to save Online Payment User', 'Error', this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  onGridActionBegin(args: any): void {
    if (args.requestType === 'filtering') {
      if (!args.currentFilterObject?.value) {
        this.filterCleared = true;
      }
    }

    if (args.requestType === 'searching') {
      if (!args.searchString) {
        this.filterCleared = true;
      }
    }
  }

  onGridDataBound(): void {
    if (!this.gridVendors) return;
    if (this.filterCleared) {
      this.fullyResetGridSelection();

      this.filterCleared = false;
      this.isFilteringOrSearching = false;
    }
  }

  private fullyResetGridSelection(): void {
    if (!this.gridVendors) {
      return;
    }
    this.gridVendors.selectionSettings.persistSelection = false;
    const selectionModule = (this.gridVendors as any).selectionModule;

    if (selectionModule) {
      if (typeof selectionModule.clearRowSelection === 'function') {
        selectionModule.clearRowSelection();
      }

      if (Array.isArray(selectionModule.selectedRowIndexes)) {
        selectionModule.selectedRowIndexes.length = 0;
      }
    }
    this.gridVendors.clearSelection();
    this.selectedVendorIds = [];
    setTimeout(() => {
      this.gridVendors.selectionSettings.persistSelection = true;
    });
  }

  resetVendorForm(): void {
    this.webinquirySettingsForm.patchValue({
      vendorMessage: '',
      vendorTab: 'current',
      vendorType: ''
    });
  }

  routeBack() {
    if (this.webinquirySettingsForm.dirty) {
      Swal.fire({
        title: $localize`Confirm`,
        text: $localize`Unsaved changes will be lost. Are you sure you want to continue?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: $localize`Yes`,
        cancelButtonText: $localize`No`,
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/dashboard']);
        }
      });
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  public created(args1) {
    this.gridVendors.filterSettings = { mode: 'Immediate', immediateModeDelay: 100 };
  }
}
